import type { WebContainer, WebContainerProcess } from '@webcontainer/api';
import { appendTerminalLine, appendTerminalProcessOutput } from '../../stores/terminal';
import { workbenchStore, type WorkbenchBuildFailure, type WorkbenchBuildFailurePhase } from '../../stores/workbench';
import {
  waitForTemplateReady,
  recordProductionBuildOutcome,
  markTemplateDependenciesInstalled,
  ensureProductionPreviewServer,
} from '../../webcontainer/index';
import { npmFrozenInstallCliArgs, webcontainerFileExistsUtf8 } from '../../webcontainer/npmFrozenInstall';
import { wcPerfMark, wcPerfMeasure } from '../../utils/workbenchPerf';
import { flushDirtyEditorFilesBeforeStream } from './writeFile';

/** Strip ANSI escape sequences (colors, cursor, line-clear) so the LLM receives clean error text. */
export function stripAnsi(s: string): string {
  // CSI sequences: ESC [ ... (letter)  — covers colors, cursor, erase-line, etc.
  // OSC sequences: ESC ] ... (BEL or ST)
  // Simple two-char escapes: ESC (char)
  return s
    .replace(/\u001b\[[0-9;]*[A-Za-z]/g, '')
    .replace(/\u001b\][^\u0007\u001b]*(?:\u0007|\u001b\\)/g, '')
    .replace(/\u001b[^[\\]]/g, '');
}

/**
 * Extract a stable signature from build errors for repeat-detection.
 * Matches the first few ERROR/error lines so minor whitespace changes don't
 * fool the deduplication.
 */
export function extractErrorSignature(errors: string): string {
  const lines = errors.split('\n');
  const meaningful = lines
    .filter((l) => l.includes('ERROR:') || l.includes('error'))
    .map((l) => l.trim())
    .slice(0, 3);
  return meaningful.join('|') || errors.slice(0, 200);
}

export interface BuildResult {
  ok: boolean;
  /** Raw terminal output (may contain ANSI codes). Used for the UI/terminal. */
  errors: string;
  output: string;
  phase?: WorkbenchBuildFailurePhase;
  /** LLM-ready formatted string — ANSI stripped, human-readable. Passed to addToolOutput. */
  formattedOutput: string;
  /** Stable error signature for repeat-error detection in the facade. */
  errorSig: string;
}

function emitBuildFailure(phase: WorkbenchBuildFailurePhase, errors: string, output: string): void {
  const detail: WorkbenchBuildFailure = { phase, errors, output, at: Date.now() };
  workbenchStore.lastBuildFailure.set(detail);
  if (typeof globalThis.window !== 'undefined') {
    globalThis.window.dispatchEvent(new CustomEvent('workbench:build-failed', { detail }));
    if (phase === 'tsc') {
      globalThis.window.dispatchEvent(new CustomEvent('workbench:tsc-failed', { detail }));
    }
  }
}

function finalizeBuildResult(result: BuildResult): BuildResult {
  recordProductionBuildOutcome(result.ok);
  return result;
}

/** A stale/cancelled build never reaches this — callers should drop it entirely. */
export class BuildAbortedError extends Error {
  constructor() {
    super('Build cancelled: the turn that requested it was superseded.');
    this.name = 'BuildAbortedError';
  }
}

/**
 * Waits for a spawned process to exit, but gives up (and kills the process) after
 * `timeoutMs` instead of hanging the whole edit turn forever if `npm install`/`vite
 * build`/`tsc` gets stuck inside the WebContainer.
 */
async function waitForExitWithTimeout(
  proc: WebContainerProcess,
  timeoutMs: number,
  label: string,
): Promise<number> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      void proc.kill();
      reject(new Error(`${label} did not finish within ${Math.round(timeoutMs / 1000)}s and was killed.`));
    }, timeoutMs);
  });
  try {
    return await Promise.race([proc.exit, timeout]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

const INSTALL_TIMEOUT_MS = 180_000;
const VITE_BUILD_TIMEOUT_MS = 120_000;
const TSC_TIMEOUT_MS = 90_000;

export async function executeBuild(
  webcontainer: WebContainer,
  onOutput?: (chunk: string) => void,
  signal?: AbortSignal,
): Promise<BuildResult> {
  if (signal?.aborted) throw new BuildAbortedError();

  appendTerminalLine(`── Build ${new Date().toLocaleTimeString()} ──`, 'info');
  workbenchStore.lastBuildFailure.set(null);

  wcPerfMark('wb:build:waitTemplate:start');
  await waitForTemplateReady();
  wcPerfMark('wb:build:waitTemplate:end');
  wcPerfMeasure('wb:build:waitTemplate:start', 'wb:build:waitTemplate:end', 'wc:executeBuild:waitTemplate');

  wcPerfMark('wb:build:flushEditor:start');
  const flushed = await flushDirtyEditorFilesBeforeStream(webcontainer);
  wcPerfMark('wb:build:flushEditor:end');
  wcPerfMeasure('wb:build:flushEditor:start', 'wb:build:flushEditor:end', 'wc:executeBuild:flushEditor');
  if (flushed > 0) {
    appendTerminalLine(
      `Synced ${flushed} edited file${flushed === 1 ? '' : 's'} from the editor into the workspace.`,
      'info',
    );
  }

  if (signal?.aborted) throw new BuildAbortedError();

  if (!workbenchStore.depsInstalled.get()) {
    const hasLock = await webcontainerFileExistsUtf8(webcontainer, 'package-lock.json');
    const npmArgs = npmFrozenInstallCliArgs(hasLock);
    appendTerminalLine(`$ npm ${npmArgs.join(' ')}`, 'info');
    const npmStart = hasLock ? 'wb:build:npm:ci:start' : 'wb:build:npm:install:start';
    const npmEnd = 'wb:build:npm:end';
    wcPerfMark(npmStart);
    const install = await webcontainer.spawn('npm', npmArgs);
    const onAbort = () => void install.kill();
    signal?.addEventListener('abort', onAbort);
    let installOutput = '';

    const installWriter = new WritableStream<string>({
      write(chunk) {
        installOutput += chunk;
        onOutput?.(chunk);
      },
    });
    const installPiping = install.output.pipeTo(installWriter);
    const installExit = await waitForExitWithTimeout(install, INSTALL_TIMEOUT_MS, 'npm install');
    signal?.removeEventListener('abort', onAbort);
    await installPiping;
    appendTerminalProcessOutput(installOutput, installExit !== 0 ? 'stderr' : 'stdout');
    wcPerfMark(npmEnd);
    wcPerfMeasure(npmStart, npmEnd, hasLock ? 'wc:executeBuild:npmCi' : 'wc:executeBuild:npmInstall');

    if (installExit !== 0) {
      const errors = `npm ${npmArgs[0]} failed:\n${installOutput}`;
      emitBuildFailure('npm-install', errors, installOutput);
      const clean = stripAnsi(installOutput);
      return finalizeBuildResult({
        ok: false,
        errors,
        output: installOutput,
        phase: 'npm-install',
        formattedOutput: `Build failed (npm ${npmArgs[0]}):\n${clean}`,
        errorSig: extractErrorSignature(clean),
      });
    }
    workbenchStore.depsInstalled.set(true);
    markTemplateDependenciesInstalled();
  } else {
    appendTerminalLine('Dependencies already installed, skipping npm install.', 'info');
  }

  if (signal?.aborted) throw new BuildAbortedError();

  appendTerminalLine('$ npx vite build', 'info');
  wcPerfMark('wb:build:vite:start');
  const viteBuild = await webcontainer.spawn('npx', ['vite', 'build']);
  const onViteAbort = () => void viteBuild.kill();
  signal?.addEventListener('abort', onViteAbort);
  let viteOutput = '';

  const viteWriter = new WritableStream<string>({
    write(chunk) {
      viteOutput += chunk;
      onOutput?.(chunk);
    },
  });
  const vitePiping = viteBuild.output.pipeTo(viteWriter);
  const viteExit = await waitForExitWithTimeout(viteBuild, VITE_BUILD_TIMEOUT_MS, 'vite build');
  signal?.removeEventListener('abort', onViteAbort);
  await vitePiping;
  appendTerminalProcessOutput(viteOutput, viteExit !== 0 ? 'stderr' : 'stdout');
  wcPerfMark('wb:build:vite:end');
  wcPerfMeasure('wb:build:vite:start', 'wb:build:vite:end', 'wc:executeBuild:vite');

  if (viteExit !== 0) {
    const errors = `Vite build failed:\n${viteOutput}`;
    emitBuildFailure('vite', errors, viteOutput);
    const clean = stripAnsi(viteOutput);
    return finalizeBuildResult({
      ok: false,
      errors,
      output: viteOutput,
      phase: 'vite',
      formattedOutput: `Build failed:\n${clean}`,
      errorSig: extractErrorSignature(clean),
    });
  }

  if (signal?.aborted) throw new BuildAbortedError();

  appendTerminalLine('$ npx tsc --noEmit', 'info');
  wcPerfMark('wb:build:tsc:start');
  const tsc = await webcontainer.spawn('npx', ['tsc', '--noEmit']);
  const onTscAbort = () => void tsc.kill();
  signal?.addEventListener('abort', onTscAbort);
  let tscOutput = '';

  const tscWriter = new WritableStream<string>({
    write(chunk) {
      tscOutput += chunk;
      onOutput?.(chunk);
    },
  });
  const tscPiping = tsc.output.pipeTo(tscWriter);
  const tscExit = await waitForExitWithTimeout(tsc, TSC_TIMEOUT_MS, 'tsc --noEmit');
  signal?.removeEventListener('abort', onTscAbort);
  await tscPiping;
  appendTerminalProcessOutput(tscOutput, tscExit !== 0 ? 'stderr' : 'stdout');
  wcPerfMark('wb:build:tsc:end');
  wcPerfMeasure('wb:build:tsc:start', 'wb:build:tsc:end', 'wc:executeBuild:tsc');

  if (tscExit !== 0) {
    const errors = `TypeScript type-check failed:\n${tscOutput}`;
    emitBuildFailure('tsc', errors, tscOutput);
    const clean = stripAnsi(tscOutput);
    return finalizeBuildResult({
      ok: false,
      errors,
      output: tscOutput,
      phase: 'tsc',
      formattedOutput: `TypeScript check failed:\n${clean}`,
      errorSig: extractErrorSignature(clean),
    });
  }

  appendTerminalLine('Build succeeded.', 'info');
  workbenchStore.lastBuildFailure.set(null);

  if (signal?.aborted) throw new BuildAbortedError();

  try {
    await ensureProductionPreviewServer(webcontainer);
  } catch (err: unknown) {
    appendTerminalLine(
      `Failed to start preview server: ${err instanceof Error ? err.message : String(err)}`,
      'stderr',
    );
  }

  return finalizeBuildResult({
    ok: true,
    errors: '',
    output: 'Build succeeded.',
    formattedOutput: 'Build succeeded.',
    errorSig: '',
  });
}
